import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { Button } from "./ui/button";
import { UtensilsCrossed, Sun, Moon, Menu, X } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const MobileLink = ({ to, testid, children }) => (
    <Link to={to} data-testid={testid} onClick={() => setMenuOpen(false)}
      className="block px-3 py-2.5 text-sm text-muted-foreground hover:text-foreground rounded-lg hover:bg-card transition-colors">
      {children}
    </Link>
  );

  return (
    <nav data-testid="main-navbar" className="sticky top-0 z-50 bg-background/75 backdrop-blur-xl border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-brand" className="flex items-center gap-2 group shrink-0">
          <UtensilsCrossed className="w-5 h-5 text-primary transition-transform duration-300 group-hover:rotate-12" />
          <span className="font-display text-xl tracking-tight text-foreground">TableDiary</span>
        </Link>
        <div className="flex items-center gap-2.5 sm:gap-6">
          <Link to="/restaurants" data-testid="nav-discover" className="hidden sm:block text-sm text-muted-foreground hover:text-foreground transition-colors">
            Discover
          </Link>
          <Link to="/tour" data-testid="nav-tour" className="hidden sm:block text-sm text-muted-foreground hover:text-foreground transition-colors">
            Product tour
          </Link>
          <button data-testid="nav-mobile-menu-btn" onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu"
            className="sm:hidden w-8 h-8 rounded-full border border-border/60 flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-border transition-colors">
            {menuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
          <button data-testid="theme-toggle-btn" onClick={toggle} aria-label="Toggle theme"
            className="w-8 h-8 rounded-full border border-border/60 flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-border transition-colors">
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          {user?.role === "diner" && (
            <Link to="/my-bookings" data-testid="nav-my-bookings" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              My Bookings
            </Link>
          )}
          {user && ["owner", "manager", "host"].includes(user.role) && (
            <Link to="/dashboard" data-testid="nav-dashboard" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              Dashboard
            </Link>
          )}
          {user?.role === "admin" && (
            <Link to="/admin" data-testid="nav-admin" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              Admin
            </Link>
          )}
          {!user ? (
            <>
              <Link to="/login" data-testid="nav-login" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Sign in
              </Link>
              <Button data-testid="nav-list-restaurant-btn" size="sm" className="rounded-full" onClick={() => navigate("/register?role=owner")}>
                List your restaurant
              </Button>
            </>
          ) : (
            <div className="flex items-center gap-3">
              <span data-testid="nav-user-name" className="text-xs text-muted-foreground hidden sm:block">{user.name}</span>
              <Button data-testid="nav-logout-btn" size="sm" variant="outline" className="rounded-full" onClick={async () => { await logout(); navigate("/"); }}>
                Sign out
              </Button>
            </div>
          )}
        </div>
      </div>
      {menuOpen && (
        <div data-testid="nav-mobile-menu" className="sm:hidden border-t border-border bg-background/95 backdrop-blur-xl px-4 py-3">
          <MobileLink to="/restaurants" testid="nav-m-discover">Discover</MobileLink>
          <MobileLink to="/tour" testid="nav-m-tour">Product tour</MobileLink>
          {!user && <MobileLink to="/login" testid="nav-m-login">Sign in</MobileLink>}
          {user?.role === "diner" && <MobileLink to="/my-bookings" testid="nav-m-bookings">My Bookings</MobileLink>}
          {user && ["owner", "manager", "host"].includes(user.role) && <MobileLink to="/dashboard" testid="nav-m-dashboard">Dashboard</MobileLink>}
          {user?.role === "admin" && <MobileLink to="/admin" testid="nav-m-admin">Admin</MobileLink>}
        </div>
      )}
    </nav>
  );
}
